import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Headphones, Mic, Award, Sparkles, ListChecks } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useI18n } from "@/contexts/I18nContext";
import FitText from "@/components/FitText";
import InstallBanner from "@/components/InstallApp";
import CommunityLiveLearning from "@/components/CommunityLiveLearning";
import { communityConfig } from "@/lib/apiClient";

const HERO = "https://static.prod-images.emergentagent.com/jobs/c83ef079-7781-41cb-a773-0e9de9fdfa34/images/d7f00ced305a2d1a0e47d4f9080412a9c404774c1fb6e8603f2f70ff7676562f.png";

const MODULE_KEYS = [
  "letter_recognition", "joined_letters", "high_frequency_words",
  "ending_vowels", "prefixes_suffixes", "joined_words",
  "pairs_clusters", "sound_vocabulary", "recitation_fluency",
];

export default function Landing() {
  const { t, lang } = useI18n();
  // Public, unauthenticated mirror of the Dashboard's community data (see
  // routes/progress.py's community_config) — a visitor hasn't logged in yet,
  // so this can't come from myProgress(). One-off fetch, no polling.
  const [community, setCommunity] = useState(null);
  useEffect(() => { communityConfig().then(setCommunity).catch(() => {}); }, []);
  return (
    <div className="min-h-screen flex flex-col" data-testid="landing-page">
      <Header />

      {/* Install strip — self-hides when the browser can't install or the app
          is already installed. `alwaysShow`: every arrival at the landing
          page shows it again regardless of a past close, first-time or
          returning visitor alike — closing it only clears the current visit,
          it doesn't write the shared dismissal Dashboard's copy respects. */}
      <InstallBanner alwaysShow />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.35] pointer-events-none" style={{ backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "right center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/95 to-transparent pointer-events-none" />

        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 pt-20 pb-14 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E7E0D2] text-xs text-[#4B5563]">
              <Sparkles className="w-3.5 h-3.5 text-[#D97706]" />
              <span className="font-medium">{t("landing.hero_badge")}</span>
            </div>
            <FitText as="h1" maxLines={2} className="font-heading mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-[#1F2937]">
              {t("landing.hero_title_read")} <span className="brand-gradient italic font-light">{t("landing.hero_title_fluently")}</span>.<br />
              {t("landing.hero_title_earn")} <span className="text-[#D97706]">{t("landing.hero_title_certificate")}</span>.
            </FitText>
            <p className="mt-6 text-base sm:text-lg text-[#4B5563] max-w-xl leading-relaxed">
              {t("landing.hero_description")}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link data-testid="cta-start" to="/login?mode=signup"
                className="inline-flex items-center gap-2.5 px-8 py-5 rounded-full bg-[#064E3B] text-white text-lg font-bold shadow-[0_10px_30px_-10px_rgba(6,78,59,0.5)] hover:bg-[#047857] hover:scale-[1.02] transition">
                {t("landing.cta_start")} <ArrowRight className="w-5 h-5" />
              </Link>
              <Link data-testid="cta-signin" to="/login?mode=signin"
                className="text-[#4B5563] font-medium hover:text-[#064E3B] hover:underline transition">
                {t("landing.cta_signin_prefix")} <span className="text-[#064E3B]">{t("landing.cta_signin_action")}</span>
              </Link>
            </div>
            <div className="mt-4">
              <Link data-testid="cta-programs" to="/login"
                className="text-sm text-[#4B5563] hover:text-[#064E3B] hover:underline transition">
                {t("landing.cta_programs")}
              </Link>
            </div>
          </div>

          {/* Right: promotional video — fixed 16:9, so `items-center` on the
              row above centres it against the taller text column rather than
              pinning it to the top. (It used to be `items-stretch`, which
              suited the earlier full-height video box; with a fixed aspect
              ratio there is nothing to stretch and the video just sat high.) */}
          <div className="lg:col-span-6">
            <div className="relative w-full max-w-[520px] mx-auto">
              <div className="absolute -inset-6 bg-[#064E3B]/5 rounded-[2.5rem] blur-2xl" />
              <div className="relative w-full aspect-video rounded-xl overflow-hidden">
                {/* Full player, same as the login hero: real controls and real
                    audio. Deliberately no `autoplay` — autoplay is only ever
                    granted with sound muted, so requesting it here would mean
                    either a silent video or a player that browsers block
                    anyway. Letting the viewer press play is what actually
                    delivers audio. */}
                <iframe
                  data-testid="hero-promo-video"
                  className="w-full h-full"
                  src="https://player.vimeo.com/video/1214948487?h=abef5c3273&controls=1&loop=1&playsinline=1&title=0&byline=0&portrait=0"
                  title="HikmahVerses promotional video"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community & Live Learning — the same section the Dashboard shows
          once signed in, surfaced here too for signed-out visitors.
          `w-full` is doing real work here, not decoration: this page's root
          is a flex column, and `mx-auto`'s auto margins disable that flex
          item's `align-items: stretch` (per spec, stretch only applies when
          NEITHER cross-axis margin is auto) — so without an explicit width,
          the section falls back to fit-content sizing off its OWN content
          instead of filling the row before max-w-[1280px] caps it. FEATURES/
          WHY-FLUENCY mask this because their content is wide enough to hit
          1152px anyway; this section's compact cards aren't, so it was
          rendering ~200px narrower and pulled off the shared left edge.
          (Same latent issue already exists on the page's own bottom CTA
          section — pre-existing, untouched, left for a separate pass.) */}
      <section className="w-full max-w-[1280px] mx-auto px-5 sm:px-8">
        <CommunityLiveLearning t={t} lang={lang} community={community} />
      </section>

      {/* FEATURES */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          <Feat icon={<ListChecks className="w-5 h-5" />} t={t("landing.feat1_title")} d={t("landing.feat1_desc")} />
          <Feat icon={<Headphones className="w-5 h-5" />} t={t("landing.feat2_title")} d={t("landing.feat2_desc")} />
          <Feat icon={<Mic className="w-5 h-5" />} t={t("landing.feat3_title")} d={t("landing.feat3_desc")} />
        </div>
      </section>

      {/* WHY FLUENCY */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-20 border-t border-[#E7E0D2]" data-testid="why-fluency">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-[#D97706] font-medium">{t("landing.why_eyebrow")}</div>
            <FitText as="h2" maxLines={2} className="font-heading mt-3 text-3xl sm:text-4xl font-semibold leading-tight text-[#1F2937]">
              {t("landing.why_title_line1")}<br/>
              <span className="brand-gradient italic font-light">{t("landing.why_title_line2")}</span>
            </FitText>
            <p className="mt-5 text-[#4B5563] leading-relaxed" dangerouslySetInnerHTML={{ __html: t("landing.why_p1_html") }} />
            <p className="mt-4 text-[#4B5563] leading-relaxed">
              {t("landing.why_p2")}
            </p>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            <WhyCard
              icon="50%"
              t={t("landing.why_card1_title")}
              d={t("landing.why_card1_desc")}
            />
            <WhyCard
              icon="9"
              t={t("landing.why_card2_title")}
              d={t("landing.why_card2_desc")}
            />
            <WhyCard
              icon="⏱"
              t={t("landing.why_card3_title")}
              d={t("landing.why_card3_desc")}
            />
            <WhyCard
              icon="✓"
              t={t("landing.why_card4_title")}
              d={t("landing.why_card4_desc")}
            />
          </div>
        </div>
      </section>

      {/* MODULES PREVIEW */}
      <section className="bg-[#064E3B] text-[#FDFBF7] py-20">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.22em] text-[#FED7AA]">{t("landing.modules_eyebrow")}</div>
              <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-semibold leading-tight">{t("landing.modules_title")}</h2>
              <p className="mt-5 text-[#FDFBF7]/75 leading-relaxed max-w-md">{t("landing.modules_desc")}</p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
              {MODULE_KEYS.map((k, i) => (
                <div key={i} className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 hover:bg-white/[0.08] transition flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#D97706]/20 text-[#FED7AA] grid place-items-center text-xs font-mono font-semibold">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-medium">{t(`landing.module_${k}`)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1024px] mx-auto px-5 sm:px-8 py-24 text-center">
        <Award className="w-12 h-12 mx-auto text-[#D97706]" />
        <h3 className="font-heading mt-6 text-3xl sm:text-4xl font-semibold text-[#1F2937]">
          {t("landing.bottom_title")}
        </h3>
        <p className="mt-4 text-[#4B5563] max-w-2xl mx-auto">
          {t("landing.bottom_desc")}
        </p>
        <Link data-testid="cta-bottom" to="/login?mode=signup"
          className="inline-flex items-center gap-2 mt-10 px-7 py-4 rounded-full bg-[#D97706] text-white font-medium hover:bg-[#B45309] transition">
          {t("landing.bottom_cta")} <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <Footer />
    </div>
  );
}

function Feat({ icon, t, d }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6 hover:shadow-[0_10px_30px_-15px_rgba(6,78,59,0.2)] transition">
      <div className="w-10 h-10 rounded-xl bg-[#FFF7ED] grid place-items-center text-[#D97706]">{icon}</div>
      <div className="font-heading text-lg font-medium mt-4 text-[#1F2937]">{t}</div>
      <div className="text-sm text-[#4B5563] mt-2 leading-relaxed">{d}</div>
    </div>
  );
}
function WhyCard({ icon, t, d }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6 hover:shadow-[0_10px_30px_-15px_rgba(6,78,59,0.2)] transition" data-testid="why-card">
      <div className="w-12 h-12 rounded-xl bg-[#064E3B] grid place-items-center text-[#FED7AA] font-heading text-lg font-semibold">{icon}</div>
      <div className="font-heading text-base font-semibold mt-4 text-[#1F2937] leading-snug">{t}</div>
      <div className="text-sm text-[#4B5563] mt-2 leading-relaxed">{d}</div>
    </div>
  );
}
